import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { format, parseJSON } from 'date-fns';
import { Network } from '@capacitor/network';
import { Observation } from '../models/observation';
import { Service } from '../models/service';
import { StorageService } from './storage.service';
import { IRequest } from '../models/request';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from './config.service';
import { SettingsService } from './settings.service';
import { HashtagService } from './hashtag.service';
import { JumpRule, NavigationJumpRule, DecisionRule, RuleStrings, TriggerCondition, ResetValue, DefaultValue } from '../models/jump-rule';

@Injectable({
  providedIn: 'root', // This ensures the service is available application-wide
})
export class Open311Service {
  public serviceList: Array<Service>;
  public requestList: Array<IRequest>;
  private apiUrl: string;
  private cachedServices: { [key: string]: Array<Service> } = {};
  private lastSettingsReload: number = 0;
  private readonly SETTINGS_RELOAD_INTERVAL = 5 * 60 * 1000; // 5 minutes

  constructor(
    public http: HttpClient,
    private storage: StorageService,
    private configService: ConfigService,
    private settingsService: SettingsService,
    private hashtagService: HashtagService
  ) {
    this.apiUrl = environment.api.baseUrl;
  }

  public get settings(): any {
    return this.configService.getSettings();
  }

  public async loadSettings(): Promise<void> {
    await this.configService.loadConfig();
  }

  public getSettings(): any {
    return this.configService.getSettings();
  }

  private async shouldReloadSettings(): Promise<boolean> {
    const networkStatus = await Network.getStatus();
    const now = Date.now();
    const timeSinceLastReload = now - this.lastSettingsReload;

    return (
      networkStatus.connected &&
      timeSinceLastReload > this.SETTINGS_RELOAD_INTERVAL
    );
  }

  private cloneServices(services: Array<Service>): Array<Service> {
    return services.map((service) => ({
      ...service,
      attributes: service.attributes
        ? service.attributes.map((attribute) => ({
            ...attribute,
            values: attribute.values
              ? attribute.values.map((value) => ({ ...value }))
              : attribute.values,
          }))
        : service.attributes,
    }));
  }

  public async getQuestions(language: string): Promise<Array<Service>> {
    // Return cached services if available
    if (this.cachedServices[language]) {
      return this.cloneServices(this.cachedServices[language]);
    }

    // Check if we need to reload settings
    if (await this.shouldReloadSettings()) {
      await this.loadSettings();
      this.lastSettingsReload = Date.now();
    }

    try {
      const url = `${this.apiUrl}/services.json`;
      const params = new HttpParams().set('lang', language);

      const services = await firstValueFrom(
        this.http.get<Array<Service>>(url, { params })
      );

      // Cache the services
      this.cachedServices[language] = this.cloneServices(services);

      return this.cloneServices(this.cachedServices[language]);
    } catch (error) {
      console.error('Error fetching services:', error);
      // Return cached services if available, otherwise empty array
      return this.cachedServices[language]
        ? this.cloneServices(this.cachedServices[language])
        : [];
    }
  }

  public async getService(
    serviceCode: string,
    language: string
  ): Promise<Service> {
    const params = new HttpParams()
      .set('locale', language)
      .set('extension', 'true')
      .set('service_code', serviceCode);

    return firstValueFrom(
      this.http.get<Service>(`${this.apiUrl}/services.json`, { params })
    );
  }

  public async getObservations(code: string) {
    // Get observations for the primary service code
    const primaryObservations = await firstValueFrom(
      this.http.get<Array<IRequest>>(`${this.apiUrl}/requests/${code}.json`)
    );

    // Get all connected service codes from services_locales
    const connectedServiceCodes = await this.getConnectedServiceCodes(code);

    // Fetch observations for all connected services
    const allObservations: Array<IRequest> = [...(primaryObservations || [])];

    // Fetch observations for all connected service codes
    if (connectedServiceCodes.length > 0) {
      for (const connectedCode of connectedServiceCodes) {
        try {
          const connectedObservations = await firstValueFrom(
            this.http.get<Array<IRequest>>(
              `${this.apiUrl}/requests/${connectedCode}.json`
            )
          );

          if (connectedObservations && connectedObservations.length > 0) {
            allObservations.push(...connectedObservations);
          }
        } catch (error) {
          console.error(
            `Error fetching observations for connected service ${connectedCode}:`,
            error
          );
        }
      }
    }

    this.requestList = allObservations;
    return this.requestList;
  }

  public async getObservationDetails(id: string): Promise<any> {
    const networkStatus = (await Network.getStatus()).connected;
    if (!networkStatus) {
      console.log('Network not available!');
      return null;
    }

    try {
      const params = new HttpParams()
        .set('extension', 'true')
        .set('service_request_id', id);
      const url = `${this.apiUrl}/requests.json`;
      const response = await firstValueFrom(this.http.get(url, { params }));
      return Array.isArray(response) && response.length > 0
        ? response[0]
        : null;
    } catch (error) {
      console.error('Error fetching observation details:', error);
      return null;
    }
  }

  public async getApiKey(): Promise<string | null> {
    // Get settings
    const settings = this.getSettings();
    if (!settings?.citobs_application) {
      return null;
    }

    try {
      // Get user settings to check for selected subrole
      const userSettings = await this.settingsService.getSettings();
      const selectedSubrole = userSettings?.subrole;

      // If there's a selected subrole, use its API key
      if (
        selectedSubrole &&
        settings.citobs_application.assigned_api_id_key_list?.[selectedSubrole]
      ) {
        return settings.citobs_application.assigned_api_id_key_list[
          selectedSubrole
        ];
      }
    } catch (error) {
      console.error('Error getting user settings:', error);
    }

    // Otherwise, use the default API key
    if (settings.citobs_application.default_api_id_key?.['data-api-key']) {
      return settings.citobs_application.default_api_id_key['data-api-key'];
    }

    return null;
  }

  private async ensureApiKey(): Promise<string> {
    const apiKey = await this.getApiKey();
    if (!apiKey) {
      throw new Error('API key not found in settings');
    }
    return apiKey;
  }

  public async postObservation(observation: Observation): Promise<any> {
    const status = (await Network.getStatus()).connected;
    if (!status) {
      throw new Error('No network connection available');
    }

    const apiKey = await this.ensureApiKey();
    const description = await this.hashtagService.buildDescription();

    return new Promise((resolve, reject) => {
      let params = new HttpParams()
        .set('api_key', apiKey)
        .set('service_code', observation.service_code)
        .set('description', description)
        .set('lat', observation.lat)
        .set('long', observation.long)
        .set('device_id', observation.deviceId);

      if (observation.media_url) {
        params = params.set('media_url', observation.media_url);
      }

      // Add optional user information
      if (observation.email) params = params.set('email', observation.email);
      if (observation.first_name)
        params = params.set('first_name', observation.first_name);
      if (observation.last_name)
        params = params.set('last_name', observation.last_name);
      if (observation.phone) params = params.set('phone', observation.phone);

      // Process attributes
      observation.attributes?.forEach((attr) => {
        if (attr.value) {
          if (attr.datatype === 'datetime') {
            attr.value = format(parseJSON(attr.value), 'yyyy-MM-dd HH:mm');
          } else if (attr.datatype === 'multivaluelist') {
            let totalvalue = 0;
            attr.values?.forEach((value) => {
              if (value.checked) {
                const y: number = +value.key;
                totalvalue += y;
              }
            });
            attr.value = totalvalue;
          }
          params = params.set(`attribute[${attr.code}]`, attr.value);
        }
      });

      const headers = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      });

      this.http
        .post(`${this.apiUrl}/requests.json`, params.toString(), { headers })
        .subscribe({
          next: (res) => resolve(res),
          error: (err) => {
            console.log('Error: ' + JSON.stringify(err));
            reject(err);
          },
        });
    });
  }

  private async filter() {
    if (
      !this.serviceList ||
      !this.getSettings()?.citobs_application
        ?.replacing_sc_placeholder_with_open311_service_codes
    ) {
      console.warn('Settings or service list not loaded');
      return;
    }

    const serviceCodesOrder = Object.values(
      this.getSettings().citobs_application
        .replacing_sc_placeholder_with_open311_service_codes
    );

    // Ensure we're working with a clean list in the current language
    this.serviceList = this.serviceList
      .filter((service: Service) => {
        // Only include services that are in the configured service codes
        return serviceCodesOrder.includes(service.service_code);
      })
      .sort((a: Service, b: Service) => {
        // Sort services according to the configured order
        return (
          serviceCodesOrder.indexOf(a.service_code) -
          serviceCodesOrder.indexOf(b.service_code)
        );
      });
  }

  private async set(key: string, value: any): Promise<void> {
    return this.storage.set(key, value);
  }

  private async get(key: string): Promise<any> {
    return this.storage.get(key);
  }

  public getDefaultLanguage(): string | null {
    if (
      this.getSettings()?.citobs_application?.default_api_id_key
        ?.service_ui_language
    ) {
      return this.getSettings().citobs_application.default_api_id_key
        .service_ui_language;
    }
    return null;
  }

  public getPreferredLanguages(): string[] {
    if (
      this.getSettings()?.citobs_application?.default_api_id_key
        ?.service_ui_languages_preferred
    ) {
      return this.getSettings().citobs_application.default_api_id_key
        .service_ui_languages_preferred;
    }
    return ['fi', 'en', 'sv']; // fallback to default supported languages
  }

  public clearServicesCache(): void {
    this.cachedServices = {};
  }

  public async getSequiDecisionServices(
    language: string
  ): Promise<Array<Service>> {
    try {
      const allServices = await this.getQuestions(language);
      const sequiServices = allServices.filter((service) =>
        service.service_code.startsWith('system_sequi_decisions_service_code_')
      );
      return sequiServices;
    } catch (error) {
      console.error('Error in getSequiDecisionServices:', error);
      return [];
    }
  }

  public async getSequiDecisionObservations(
    services: Array<Service>
  ): Promise<{
    [serviceCode: string]: {
      observations: Array<IRequest>;
      jumpRules: JumpRule[];
    };
  }> {
    const result: {
      [serviceCode: string]: {
        observations: Array<IRequest>;
        jumpRules: JumpRule[];
      };
    } = {};
    const managerApiId =
      this.settings?.citobs_application?.submittergroup
        ?.submittergroup_manager_api_id;
    const privilegeServiceRootIds =
      this.settings?.citobs_application?.submittergroup
        ?.submitter_privilege_service_root_ids || [];

    if (!managerApiId && privilegeServiceRootIds.length === 0) {
      console.error(
        'Neither submittergroup_manager_api_id nor submitter_privilege_service_root_ids found in settings'
      );
      return result;
    }

    // Storage for all valid observations and their jump rules
    const jumpRulesByService: { [serviceCode: string]: JumpRule[] } = {};

    for (const service of services) {
      try {
        // First get basic observation list
        const basicObservations = await this.getObservations(
          service.service_code
        );
        if (basicObservations) {
          // Load detailed version of each observation
          for (const obs of basicObservations) {
            const detailedObs = await this.getObservationDetails(
              obs.service_request_id
            );
            const obsApiId = detailedObs?.attributes?.['Open311_API_ID'];

            // Check if the observation's API ID matches either the manager API ID or any of the privilege service root IDs
            if (
              detailedObs &&
              (obsApiId === managerApiId ||
                privilegeServiceRootIds.includes(obsApiId))
            ) {
              // Find the target service code from attributes
              const targetServiceCodeAttr = Object.entries(
                detailedObs.attributes
              ).find(([key]) =>
                key.startsWith('system_sequi_uiservicesc_string_')
              );

              if (targetServiceCodeAttr) {
                const targetServiceCode = targetServiceCodeAttr[1] as string;

                // Handle wildcard service codes
                const isWildcard = targetServiceCode.endsWith('*');
                const serviceCodePrefix = isWildcard
                  ? targetServiceCode.slice(0, -1)
                  : targetServiceCode;

                // For wildcard codes, we need to add the observation to all matching service codes
                if (isWildcard) {
                  services.forEach((matchingService) => {
                    if (
                      matchingService.service_code.startsWith(serviceCodePrefix)
                    ) {
                      if (!result[matchingService.service_code]) {
                        result[matchingService.service_code] = {
                          observations: [],
                          jumpRules: [],
                        };
                      }

                      // Add observation and its jump rules
                      result[matchingService.service_code].observations.push(
                        detailedObs
                      );

                      // Parse jump rules from this observation
                      const jumpRules = this.parseJumpRules(detailedObs);
                      if (jumpRules) {
                        if (!jumpRulesByService[matchingService.service_code]) {
                          jumpRulesByService[matchingService.service_code] = [];
                        }
                        jumpRulesByService[matchingService.service_code].push(
                          ...jumpRules
                        );
                      }
                    }
                  });
                } else {
                  // Non-wildcard logic
                  if (!result[targetServiceCode]) {
                    result[targetServiceCode] = {
                      observations: [],
                      jumpRules: [],
                    };
                  }

                  // Add observation and its jump rules
                  result[targetServiceCode].observations.push(detailedObs);

                  // Parse jump rules from this observation
                  const jumpRules = this.parseJumpRules(detailedObs);
                  if (jumpRules) {
                    if (!jumpRulesByService[targetServiceCode]) {
                      jumpRulesByService[targetServiceCode] = [];
                    }
                    jumpRulesByService[targetServiceCode].push(...jumpRules);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error processing service:', service.service_code, error);
      }
    }

    // Add all collected jump rules to the result
    Object.keys(result).forEach((serviceCode) => {
      if (jumpRulesByService[serviceCode]) {
        result[serviceCode].jumpRules = jumpRulesByService[serviceCode];
      }
    });

    return result;
  }

  /**
   * Recursively finds all connected service codes for a given service code
   * based on the services_locales configuration
   *
   * @param serviceCode The service code to find connections for
   * @param processedCodes Set of codes already processed (to prevent infinite recursion)
   * @returns Array of connected service codes
   */
  private async getConnectedServiceCodes(
    serviceCode: string,
    processedCodes: Set<string> = new Set()
  ): Promise<string[]> {
    // Add current code to processed set to prevent infinite recursion
    processedCodes.add(serviceCode);

    const connectedCodes: string[] = [];
    const settings = this.getSettings();

    // If settings or services_locales doesn't exist, return empty array
    if (!settings?.citobs_application?.services_locales) {
      return connectedCodes;
    }

    // Check each language section in services_locales
    for (const language of Object.keys(
      settings.citobs_application.services_locales
    )) {
      const languageServices =
        settings.citobs_application.services_locales[language];

      // Check if current service code has connected services in this language
      if (languageServices[serviceCode]) {
        const connectedCode = languageServices[serviceCode];
        if (!processedCodes.has(connectedCode)) {
          connectedCodes.push(connectedCode);

          // Recursively check for further connections
          const furtherConnections = await this.getConnectedServiceCodes(
            connectedCode,
            processedCodes
          );
          connectedCodes.push(...furtherConnections);
        }
      }

      // Also check if our service code is a value in the services_locales (reverse lookup)
      for (const [srcCode, targetCode] of Object.entries(languageServices)) {
        if (targetCode === serviceCode && !processedCodes.has(srcCode)) {
          connectedCodes.push(srcCode);

          // If we found a connection, recursively check for further connections
          const furtherConnections = await this.getConnectedServiceCodes(
            srcCode,
            processedCodes
          );
          connectedCodes.push(...furtherConnections);
        }
      }
    }

    // Return unique list of connected service codes
    return [...new Set(connectedCodes)];
  }

  private parseJumpRules(observation: IRequest): JumpRule[] {
    if (!observation.attributes) return [];

    const rules: JumpRule[] = [];
    const ruleStrings: RuleStrings = {};

    // First collect all relevant rule strings
    Object.entries(observation.attributes).forEach(([key, value]) => {
      if (key.startsWith('system_sequi_decidingattributes_string_')) {
        ruleStrings.decidingAttributes = value as string;
      } else if (key.startsWith('system_sequi_resetedattrsvalues_string_')) {
        ruleStrings.resetValues = value as string;
      } else if (key.startsWith('system_sequi_defaultingattrsvalues_string_')) {
        ruleStrings.defaultValues = value as string;
      } else if (key.startsWith('system_sequi_defaultingattrsvalues_')) {
        // Handle special defaulting attributes for list types
        const listDefaultValues = value as string;
        if (listDefaultValues) {
          // Append to existing default values if any
          ruleStrings.defaultValues = ruleStrings.defaultValues
            ? ruleStrings.defaultValues + ';' + listDefaultValues
            : listDefaultValues;
        }
      } else if (key.startsWith('system_sequi_nowshownattributes_string_')) {
        ruleStrings.hiddenQuestions = value as string;
      } else if (key.startsWith('system_sequi_uijumpatattributes_string_')) {
        ruleStrings.jumpSource = value as string;
      } else if (key.startsWith('system_sequi_uijumptoattribute_string_')) {
        ruleStrings.jumpTarget = value as string;
      } else if (key.startsWith('system_sequi_servicekeywords_string_')) {
        ruleStrings.serviceKeywords = value as string;
      } else if (key.startsWith('system_sequi_uiservicesc_string_')) {
        ruleStrings.serviceCodes = value as string;
      }
    });

    // Parse keywords and service codes if present
    const keywords =
      ruleStrings.serviceKeywords
        ?.split(',')
        .map((k) => k.trim())
        .filter((k) => k) || [];
    const serviceCodes =
      ruleStrings.serviceCodes
        ?.split(',')
        .map((c) => c.trim())
        .filter((c) => c) || [];

    // Handle navigation jump rules
    if (ruleStrings.jumpSource && ruleStrings.jumpTarget) {
      const [sourceAttribute, valuesStr] = ruleStrings.jumpSource.split(':');
      if (sourceAttribute && valuesStr) {
        const triggerValues = valuesStr.split(',').filter((v) => v);
        const rule: NavigationJumpRule = {
          type: 'jump',
          sourceAttribute,
          triggerValues,
          targetAttribute: ruleStrings.jumpTarget,
          keywords,
          serviceCodes,
        };
        rules.push(rule);
      }
    }

    // Handle decision rules
    if (ruleStrings.decidingAttributes) {
      const triggerConditions: TriggerCondition[] =
        ruleStrings.decidingAttributes.split(';').map((condition) => {
          const [questionId, valuesStr] = condition.trim().split(':');
          return {
            questionId,
            values: valuesStr.split(',').filter((v) => v),
          };
        });

      const rule: DecisionRule = {
        type: 'decide',
        triggerConditions,
        resetValues: [],
        defaultValues: [],
        hideQuestions: [],
        keywords,
        serviceCodes,
      };

      // Parse reset values
      if (ruleStrings.resetValues) {
        rule.resetValues = ruleStrings.resetValues.split(';').map((reset) => {
          const [questionId, valuesStr] = reset.trim().split(':');
          return {
            questionId,
            values: valuesStr
              ? valuesStr.split(',').filter((v) => v)
              : undefined,
          };
        });
      }

      // Parse default values
      if (ruleStrings.defaultValues) {
        rule.defaultValues = ruleStrings.defaultValues
          .split(';')
          .map((defaultVal) => {
            const [questionId, value] = defaultVal.trim().split(':');
            return {
              questionId,
              value,
            };
          });
      }

      // Parse hidden questions
      if (ruleStrings.hiddenQuestions) {
        rule.hideQuestions = ruleStrings.hiddenQuestions
          .split(';')
          .map((q) => q.trim())
          .filter((q) => q);
      }

      rules.push(rule);
    }

    return rules;
  }
}
